import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { api } from "@/lib/api";
import { Activity, Target, Loader2, ArrowRight, Layers } from "lucide-react";

export default function GeoPortfolioPage() {
  const { token } = useParams();
  const [data, setData] = useState(undefined);

  useEffect(() => {
    api.get(`/public/geophys/portfolio/${token}`).then((r) => setData(r.data))
      .catch((e) => setData(e?.response?.status === 410 ? "expired" : null));
  }, [token]);

  if (data === undefined) return <div className="min-h-screen grid place-items-center bg-[#0B1420] text-slate-300"><Loader2 className="animate-spin mr-2" /> Loading…</div>;
  if (data === "expired") return <div className="min-h-screen grid place-items-center bg-[#0B1420] text-slate-300 px-6 text-center" data-testid="geo-portfolio-expired">This portfolio link has expired. Please request an updated link.</div>;
  if (data === null) return <div className="min-h-screen grid place-items-center bg-[#0B1420] text-slate-300" data-testid="geo-portfolio-invalid">This link is invalid or has been revoked.</div>;

  const teasers = data.teasers || [];
  return (
    <div className="min-h-screen bg-[#0B1420] text-slate-100" style={{ fontFamily: "IBM Plex Sans, sans-serif" }} data-testid="geo-portfolio-page">
      <div className="border-b border-white/10">
        <div className="max-w-6xl mx-auto px-6 py-4 flex items-center gap-3">
          {data.logo ? <img src={data.logo} alt="logo" className="h-9 max-w-[150px] object-contain" />
            : <Activity className="text-[#C20511]" size={24} />}
          <div className="min-w-0">
            <div className="font-extrabold tracking-tight truncate" style={{ fontFamily: "Manrope, sans-serif" }}>{data.company || "SYMIN"}</div>
            <div className="text-[11px] text-slate-400">Exploration Target Portfolio</div>
          </div>
          <div className="ml-auto text-[11px] uppercase tracking-widest text-[#C20511] font-semibold hidden sm:block">
            {data.count} project{data.count === 1 ? "" : "s"}
          </div>
        </div>
      </div>

      <div className="max-w-6xl mx-auto px-6 py-8 space-y-8">
        <div>
          <h1 className="text-3xl sm:text-4xl font-extrabold tracking-tight" style={{ fontFamily: "Manrope, sans-serif" }}>Priority Target Portfolio</h1>
          <p className="text-slate-400 mt-2 text-sm max-w-2xl">A curated set of multi-method geophysical interpretations. Open any project to explore its coincident priority targets in detail.</p>
          {data.intro && <p className="text-slate-300 mt-3 text-sm max-w-3xl border-l-2 border-[#C20511]/60 pl-3" data-testid="geo-portfolio-intro">{data.intro}</p>}
        </div>

        {teasers.length === 0 ? (
          <div className="border border-white/10 rounded-2xl py-20 text-center text-slate-400" data-testid="geo-portfolio-empty">
            <Layers size={40} className="mx-auto mb-3 opacity-40" /> No shared targets are currently published.
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5" data-testid="geo-portfolio-grid">
            {teasers.map((t, i) => (
              <Link to={`/geo-teaser/${t.token}`} key={t.token || i} data-testid={`geo-portfolio-card-${i}`}
                className="group rounded-2xl overflow-hidden border border-white/10 bg-white/5 hover:bg-white/10 transition-colors">
                <div className="aspect-[4/3] bg-white overflow-hidden">
                  {t.map ? <img src={t.map} alt={t.name} className="w-full h-full object-cover group-hover:scale-[1.03] transition-transform duration-300" />
                    : <div className="w-full h-full grid place-items-center text-slate-300"><Target size={30} /></div>}
                </div>
                <div className="p-4">
                  <div className="flex items-start justify-between gap-2">
                    <div className="min-w-0">
                      <div className="font-extrabold truncate" style={{ fontFamily: "Manrope, sans-serif" }}>{t.name || "Untitled project"}</div>
                      <div className="text-[11px] text-slate-400 truncate">{[t.project_number, t.client_name, t.country].filter(Boolean).join(" · ") || t.commodity || "-"}</div>
                    </div>
                    {t.top_score != null && (
                      <div className="shrink-0 text-right"><div className="text-xl font-extrabold text-[#C20511] leading-none">{t.top_score}</div><div className="text-[9px] text-slate-500 uppercase tracking-wide">top score</div></div>
                    )}
                  </div>
                  <div className="mt-3 flex flex-wrap gap-1.5">
                    {(t.methods || []).slice(0, 4).map((m) => <span key={m} className="text-[10px] font-semibold rounded-full px-2 py-0.5 bg-white/10 text-slate-200">{m}</span>)}
                  </div>
                  <div className="mt-3 flex items-center justify-between text-[11px] text-slate-400">
                    <span>{t.coincident_count} coincident · {t.total_datasets} dataset{t.total_datasets === 1 ? "" : "s"}</span>
                    <span className="inline-flex items-center gap-1 text-[#C20511] font-semibold group-hover:gap-1.5 transition-all">View <ArrowRight size={12} /></span>
                  </div>
                </div>
              </Link>
            ))}
          </div>
        )}

        <p className="text-[11px] text-slate-500 text-center pt-2 pb-8 border-t border-white/10">
          CONFIDENTIAL - Geophysical anomalies / exploration targets requiring field verification; not a statement of Mineral Resources or Reserves. &nbsp;·&nbsp; Powered by <span className="text-[#C20511] font-semibold">{data.company || "SYMIN"}</span>
        </p>
      </div>
    </div>
  );
}
